/**
 * `/teacher/courseware` — list of AI-drafted courseware that the teacher
 * needs to review.
 *
 * Sprint 1 ships only the *list view* — there is no draft generator yet
 * (that lands with the Courseware Agent in Sprint 3). Every row is
 * stamped with the canonical AI-draft labels.
 */

import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';
import { AiDraftLabels } from '../../components/AiDraftLabels';

export function Courseware() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.listCoursewareDrafts(token),
    [client, token],
  );

  if (state.status === 'loading') {
    return <LoadingState label="Loading courseware drafts…" />;
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.data.length === 0) {
    return (
      <EmptyState
        title="No courseware drafts yet"
        description="The Courseware Agent ships in Sprint 3. Until then this list will only have hand-curated mock drafts."
      />
    );
  }

  return (
    <section className="page" data-testid="teacher-courseware">
      <header className="page__header">
        <h1>Courseware drafts</h1>
        <p className="muted">
          Every item below is AI-generated. Review and edit each draft before
          using it with students. Nothing here has been published anywhere.
        </p>
      </header>

      <ul className="list" aria-label="Courseware drafts">
        {state.data.map((draft) => (
          <li
            key={draft.id}
            className="list__item"
            data-testid="courseware-draft"
          >
            <div className="list__row">
              <div className="list__title">
                <h2>{draft.title}</h2>
                <p className="muted">
                  {draft.courseName} · last updated{' '}
                  <time dateTime={draft.updatedAt}>
                    {formatDate(draft.updatedAt)}
                  </time>
                </p>
              </div>
              <AiDraftLabels status={draft.status} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  } catch {
    return iso;
  }
}
